import { ThemeEvents } from '@theme/events';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    closestSection.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    closestSection.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate);
  }

  /**
   * Handles product select event and updates the price.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const { priceContainer, volumePricingNote } = this.refs;
        // Find the new product-price element in the updated HTML
        const newProductPrice = html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`);
        if (!newProductPrice) return;

        // Update price container
        const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
        if (newPrice && priceContainer) {
          priceContainer.replaceWith(newPrice);
        }

        // Update volume pricing note
        const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');
        if (!newNote) {
          volumePricingNote?.remove();
        } else if (!volumePricingNote) {
          // Use newPrice since priceContainer was just replaced and now points to the detached element
          newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
        } else {
          volumePricingNote.replaceWith(newNote);
        }

        // Update installments (SPI banner) variant ID to trigger payment terms re-render
        const installmentsInput = /** @type {HTMLInputElement|null} */ (
          this.querySelector(`#product-form-installment-${this.dataset.blockId} input[name="id"]`)
        );
        if (installmentsInput) {
          installmentsInput.value = detail.resource?.id ?? '';
          installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // The selected quantity may differ from 1, so reapply it to the refreshed price.
        const quantityInput = this.closest('.shopify-section, dialog')?.querySelector(
          'product-form-component input[name="quantity"]',
        );
        const quantity = Number.parseInt(quantityInput?.value ?? '', 10);
        if (Number.isFinite(quantity) && quantity > 1) this.#updateDisplayedPrice(quantity);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-price] Event promise rejected:', error);
      });
  };

  /**
   * Handles quantity selector updates and refreshes the displayed price so it
   * reflects the selected quantity (unit price × quantity).
   * @param {CustomEvent} event - The quantity selector update event.
   */
  #handleQuantityUpdate = (event) => {
    const { quantity, cartLine } = event.detail ?? {};
    // Cart line quantity updates are handled by the cart components, not the product price display.
    if (cartLine != null) return;
    if (typeof quantity !== 'number' || !Number.isFinite(quantity) || quantity < 1) return;
    this.#updateDisplayedPrice(quantity);
  };

  /**
   * Updates the displayed price to the unit price multiplied by the given quantity.
   * Skips volume pricing displays, which show a price range instead.
   * @param {number} quantity - The selected quantity.
   */
  #updateDisplayedPrice(quantity) {
    // Query the live container: refs can point to a detached node after a section update.
    const priceContainer = this.querySelector('[ref="priceContainer"]') ?? this.refs.priceContainer;
    if (!priceContainer || priceContainer.dataset.hasVolumePricing === 'true') return;

    const unitPriceMinor = Number.parseInt(priceContainer.dataset.unitPriceMinor ?? '', 10);
    const currency = priceContainer.dataset.currency;
    const moneyFormat = priceContainer.dataset.moneyFormat;
    if (!Number.isFinite(unitPriceMinor) || !currency || !moneyFormat) return;

    const formattedPrice = formatMoney(unitPriceMinor * quantity, moneyFormat, currency);
    const priceElement = priceContainer.querySelector(':scope > .price');
    if (!priceElement) return;

    // The visible price is the trailing text node, after the visually-hidden label.
    const textNodes = [...priceElement.childNodes].filter((node) => node.nodeType === Node.TEXT_NODE);
    const textNode = textNodes.at(-1);
    if (textNode) {
      textNode.nodeValue = formattedPrice;
    } else {
      priceElement.append(formattedPrice);
    }
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
